import { calcularTurnoDelDia, inicioDeSemana, addDays, toDateKey, overrideDelDia } from "../../utils/calendario";
import { TIPO_TURNO_LABEL } from "../../lib/turnoCockpit";

// La semana de lunes a domingo, en celdas con el color de su turno. Es LA tira,
// una sola: hasta 2026-08-09 vivía escrita dos veces (el Home y el riel de Mi
// Turno) y se veían iguales por casualidad, no por diseño.
//
// Reglas que la tira arrastra desde el Home y que no se negocian:
//   · Respeta los overrides (días que el usuario cambió a mano). Jamás afirmar
//     un turno que el calendario ya no tiene, ni siquiera en pasado.
//   · Los días ya pasados se atenúan: la semana se lee como línea de tiempo.
//     Atenuar ubica el presente, no reprocha nada.
//   · El día libre tiene color propio (sage). Una celda en blanco se lee como
//     "falta un dato", no como "estás libre".
//
// `onDia` es opcional: donde el destino sabe abrirse en un día concreto (el
// Home) cada celda es un botón; donde no (MiPAE en turno, cuyo calendario solo abre
// el mes), las celdas son estáticas y quien navega es el contenedor. Un botón
// que promete abrir el jueves y abre el mes es una promesa incumplida.
//
// Dirección B (2026-09-10, elegida por el fundador entre tres maquetas): la
// celda ya no es una caja con fondo y borde; el turno es un CÍRCULO lleno
// alrededor del número. La regla "una celda en blanco se lee como falta un
// dato" pasa a: el día libre se lee libre porque el círculo NO está, y la
// tarjeta de arriba (TarjetaHoy) lo dice con palabras.

const DIAS_LETRA = ["L", "M", "M", "J", "V", "S", "D"];

// Círculo relleno con el color del turno; texto en `text-paper` (no
// `text-white`: en tema oscuro los `--shift-*-color` son claros, blanco no
// contrasta encima).
export const CIRCULO_TURNO = {
  largo:  "bg-[var(--shift-work-color)] text-paper",
  diurno: "bg-[var(--shift-day-color)] text-paper",
  noche:  "bg-[var(--shift-night-color)] text-paper",
  24:     "bg-[var(--shift-night-color)] text-paper",
};
export const LETRA_TURNO = {
  largo:  "text-[var(--shift-work-color)]",
  diurno: "text-[var(--shift-day-color)]",
  noche:  "text-[var(--shift-night-color)]",
  24:     "text-[var(--shift-night-color)]",
};
export const TIPO_LETRA = { largo: "L", noche: "N", diurno: "D", 24: "24" };

// Los 7 días de la semana de `ahora`, con override aplicado. Exportado porque
// quien dibuja la tira suele necesitar también el turno de HOY (la tarjeta de
// arriba), y sacarlo de la MISMA lista evita que las dos piezas se contradigan.
export function diasDeLaSemana(rotacion, overrides = null, ahora = new Date()) {
  const lunes = inicioDeSemana(ahora);
  const hoyKey = toDateKey(ahora);
  return Array.from({ length: 7 }, (_, i) => {
    const fecha = addDays(lunes, i);
    const key = toDateKey(fecha);
    return {
      fecha,
      key,
      turno: calcularTurnoDelDia(fecha, rotacion, overrideDelDia(fecha, overrides)),
      esHoy: key === hoyKey,
      pasado: key < hoyKey,
    };
  });
}

export default function TiraSemana({ dias, onDia = null, className = "" }) {
  return (
    <div className={`grid grid-cols-7 gap-1.5 ${className}`}>
      {dias.map((d) => {
        const etiqueta = `${d.fecha.toLocaleDateString("es-CL", { weekday: "long", day: "numeric" })}: ${
          d.turno.tipo === "libre" ? "libre" : TIPO_TURNO_LABEL[d.turno.tipo] || d.turno.tipo
        }${d.esHoy ? " (hoy)" : ""}`;
        const conTurno = d.turno.tipo !== "libre";
        const clases = `flex flex-col items-center gap-1 py-1.5 min-h-[54px] rounded-tk-sm ${d.pasado ? "opacity-45" : ""}`;
        const numeroClases = [
          "inline-flex h-7 w-7 items-center justify-center rounded-full text-small font-sans font-semibold tabular-nums leading-none",
          conTurno ? (CIRCULO_TURNO[d.turno.tipo] || "") : "text-ink",
          d.esHoy && !conTurno ? "bg-accent text-paper font-bold" : "",
          d.esHoy && conTurno ? "ring-2 ring-accent ring-offset-2 ring-offset-paper font-bold" : "",
        ].filter(Boolean).join(" ");
        const cuerpo = (
          <>
            <span className="text-10px font-sans leading-none text-ink-faint">{DIAS_LETRA[(d.fecha.getDay() + 6) % 7]}</span>
            <span className={numeroClases}>{d.fecha.getDate()}</span>
            <span className={`text-9px font-bold font-sans leading-none h-[9px] ${conTurno ? (LETRA_TURNO[d.turno.tipo] || "") : ""}`}>
              {conTurno ? (TIPO_LETRA[d.turno.tipo] || "") : d.esHoy ? "·" : ""}
            </span>
          </>
        );

        if (!onDia) {
          // Sin aria-hidden a propósito: si la tira va dentro de un botón con
          // su propia etiqueta, esa etiqueta manda igual; si va suelta, el
          // contenido se sigue pudiendo leer.
          return (
            <span key={d.key} className={clases} title={etiqueta}>
              {cuerpo}
            </span>
          );
        }
        return (
          <button
            key={d.key}
            type="button"
            onClick={() => onDia(d.fecha)}
            aria-label={etiqueta}
            aria-current={d.esHoy ? "date" : undefined}
            className={`${clases} transition-all active:scale-[0.96]`}
          >
            {cuerpo}
          </button>
        );
      })}
    </div>
  );
}
